/**
 * SuggestedTodosConfirm — checkbox list of AI-suggested todos under the Oppdatering composer.
 * User must confirm before tasks are created via tasksApi. Never auto-creates.
 * Created items stay visible as «Opprettet» (non-actionable); pending stay createable.
 * Todos can be created before Publiser; optional statusUpdateId links them when present.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  CheckCircleIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolidIcon } from "@heroicons/react/24/solid";
import { tasksApi } from "../../../api/tasks";
import { TWclassNames } from "../../utils/Div";

function normalizeTitle(title) {
  return String(title || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function todoKey(todo) {
  const title = normalizeTitle(todo?.title || "");
  const place = todo?.project_id || todo?.customer_id || "";
  return place ? `${title}|${place}` : title;
}

function todoPlaceLabel(todo) {
  return (
    todo?.project_name ||
    todo?.customer_name ||
    todo?.module_tags?.[0]?.name ||
    ""
  );
}

function addModuleTag(tags, seen, moduleName, param) {
  if (!moduleName || param == null || param === "") return;
  const key = `${moduleName}:${param}`;
  if (seen.has(key)) return;
  seen.add(key);
  tags.push({ module: moduleName, module_param: String(param) });
}

export function SuggestedTodosConfirm({
  todos = [],
  /** Already-created suggestions: `{ title, taskId?, status: 'created' }` */
  createdTodos = [],
  statusUpdateId = null,
  projectId = null,
  customerId = null,
  onCreated,
  onDismiss,
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState({});
  const [creating, setCreating] = useState(false);

  const pending = Array.isArray(todos) ? todos : [];
  const created = Array.isArray(createdTodos) ? createdTodos : [];
  const hasPending = pending.length > 0;
  const hasCreated = created.length > 0;

  // Preserve checked/unchecked state across debounced AI refreshes; only default
  // newly appeared keys to selected.
  useEffect(() => {
    setSelected((prev) => {
      const next = {};
      pending.forEach((todo) => {
        if (!todo?.title) return;
        const key = todoKey(todo);
        next[key] = Object.prototype.hasOwnProperty.call(prev, key)
          ? prev[key]
          : true;
      });
      return next;
    });
  }, [todos]);

  if (!hasPending && !hasCreated) return null;

  const toggle = (key) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const confirmed = pending.filter(
    (todo) => selected[todoKey(todo)] && todo?.title
  );
  // Creatable before Publiser — statusUpdateId is optional (linked on publish if missing).
  const canCreate = confirmed.length > 0 && !creating;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    let ok = 0;
    let fail = 0;
    const createdTasks = [];
    const createdSuggestionRows = [];

    for (const todo of confirmed) {
      const module_tags = [];
      const seen = new Set();
      const todoProjectId = todo.project_id || projectId || null;
      const todoCustomerId = todo.customer_id || customerId || null;

      addModuleTag(module_tags, seen, "status_update", statusUpdateId);
      addModuleTag(module_tags, seen, "project", todoProjectId);
      addModuleTag(module_tags, seen, "customer", todoCustomerId);
      (Array.isArray(todo.module_tags) ? todo.module_tags : []).forEach((tag) => {
        addModuleTag(
          module_tags,
          seen,
          tag?.module,
          tag?.module_param || tag?.id
        );
      });

      const payload = {
        title: String(todo.title).trim().slice(0, 100),
        project_id: todoProjectId || undefined,
        customer_id: todoCustomerId || undefined,
        module_tags: module_tags.length ? module_tags : undefined,
      };

      try {
        const response = await tasksApi.createTask(payload);
        if (response?.data?.success) {
          ok += 1;
          const task = response.data.data;
          createdTasks.push(task);
          createdSuggestionRows.push({
            title: String(todo.title).trim(),
            taskId: task?.id ?? null,
            status: "created",
            // Keep the suggestion's own ids so todoKey matches the pending row
            // (composer project/customer is only a create fallback).
            project_id: todo.project_id || null,
            customer_id: todo.customer_id || null,
          });
        } else {
          fail += 1;
        }
      } catch {
        fail += 1;
      }
    }

    setCreating(false);
    if (ok > 0) {
      toast.success(
        t("oppdatering.todos_created", {
          count: ok,
          defaultValue: `${ok} oppgave(r) opprettet`,
        })
      );
      onCreated?.(createdTasks, createdSuggestionRows);
    }
    if (fail > 0) {
      toast.error(
        t("oppdatering.todos_create_failed", {
          count: fail,
          defaultValue: `Kunne ikke opprette ${fail} oppgave(r)`,
        })
      );
    }
  };

  return (
    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
      <div className="mb-2 flex items-center gap-2">
        <ClipboardDocumentListIcon className="h-5 w-5 text-[#1463C7]" />
        <h4 className="text-sm font-semibold text-slate-900">
          {t("Foreslåtte oppgaver")}
        </h4>
        {hasPending && (
          <span className="text-xs text-slate-500">
            {t("Velg hvilke som skal opprettes")}
          </span>
        )}
      </div>

      {hasPending && (
        <ul className="flex flex-col gap-1.5">
          {pending.map((todo) => {
            const key = todoKey(todo);
            const checked = !!selected[key];
            return (
              <li key={key}>
                <label
                  className={TWclassNames(
                    "flex cursor-pointer items-start gap-2.5 rounded-xl border bg-white px-3 py-2.5 transition-colors",
                    checked
                      ? "border-[#BFDCF7] bg-[#EAF3FD]/40"
                      : "border-slate-200"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(key)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-slate-800">
                      {todo.title}
                    </span>
                    {todoPlaceLabel(todo) && (
                      <span className="mt-0.5 block text-xs text-slate-500">
                        {todoPlaceLabel(todo)}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {hasCreated && (
        <div className={hasPending ? "mt-3 border-t border-slate-200 pt-3" : ""}>
          {hasPending && (
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("oppdatering.todos_created_section", {
                defaultValue: "Opprettede oppgaver",
              })}
            </p>
          )}
          <ul className="flex flex-col gap-1.5">
            {created.map((todo, i) => {
              const title = todo?.title || "";
              const taskId = todo?.taskId;
              return (
                <li key={`created:${i}:${title}`}>
                  <div className="flex items-start gap-2.5 rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2.5">
                    <CheckCircleSolidIcon
                      className="mt-0.5 h-4 w-4 shrink-0 text-[#0B7A3B]"
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      {taskId ? (
                        <Link
                          to="/dashboard/todos"
                          className="text-sm font-medium text-slate-800 underline-offset-2 hover:underline"
                        >
                          {title}
                        </Link>
                      ) : (
                        <span className="text-sm text-slate-800">{title}</span>
                      )}
                    </div>
                    <span className="inline-flex shrink-0 items-center rounded-full bg-[#BBF7D0] px-2 py-0.5 text-xs font-semibold text-[#0B7A3B]">
                      {t("oppdatering.todo_created_badge", {
                        defaultValue: "Opprettet",
                      })}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-xl px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            {hasPending
              ? t("Hopp over")
              : t("Lukk", { defaultValue: "Lukk" })}
          </button>
        )}
        {hasPending && (
          <button
            type="button"
            onClick={handleCreate}
            disabled={!canCreate}
            className={TWclassNames(
              "inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-all",
              canCreate
                ? "bg-[linear-gradient(135deg,#1463C7,#2D8BE8)] text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.6)]"
                : "cursor-not-allowed bg-slate-100 text-slate-400"
            )}
          >
            <CheckCircleIcon className="h-5 w-5" />
            {creating
              ? t("Oppretter…")
              : t("Opprett valgte", { count: confirmed.length })}
          </button>
        )}
      </div>
    </div>
  );
}

export default SuggestedTodosConfirm;
